import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import CompanyIntentFields from "../../components/CompanyIntentFields/CompanyIntentFields";
import LeadSourceFields from "../../components/LeadSourceFields/LeadSourceFields";
import { uploadFile } from "../../config/api";
import { uploadAccept, validateUploadFile } from "../../config/uploads";
import { extractApiErrorMessage, getApiErrorMessage, getApiFieldErrors } from "../../lib/apiError";
import {
  type CompanyIntentErrors,
  EMPTY_INTENT,
  INTENT_FIELD_KEYS,
  intentPayload,
  pickIntentErrors,
  validateIntent,
} from "../../lib/companyIntent";
import {
  EMPTY_LEAD_SOURCE,
  LEAD_SOURCE_FIELD_KEYS,
  type LeadSourceErrors,
  leadSourcePayload,
  pickLeadSourceErrors,
  validateLeadSource,
} from "../../lib/leadSource";
import { useCompaniesRegisterCreate } from "../../services/companies/generated/companies/companies";
import "./AuthPage.css";

interface FormErrors {
  companyName?: string;
  companyEmail?: string;
}

const CompanyRegistrationPage = () => {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const [companyName, setCompanyName] = useState("");
  const [companyIndustry, setCompanyIndustry] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyDescription, setCompanyDescription] = useState("");
  const [companyAvatar, setCompanyAvatar] = useState("");
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const [errors, setErrors] = useState<FormErrors>({});
  // Corridor and intent (WP1.5).
  const [intent, setIntent] = useState(EMPTY_INTENT);
  const [intentErrors, setIntentErrors] = useState<CompanyIntentErrors>({});
  // Where the company heard about us (WP1.9).
  const [leadSource, setLeadSource] = useState(EMPTY_LEAD_SOURCE);
  const [leadSourceErrors, setLeadSourceErrors] = useState<LeadSourceErrors>({});

  // Use generated TanStack Query mutation hook
  const registerMutation = useCompaniesRegisterCreate({
    mutation: {
      onSuccess: (data) => {
        setSuccessMessage(data.message ?? null);
        setSubmitted(true);
        toast.success(t("register.toastSuccess"));
      },
      onError: (error) => {
        // Inline errors for the fields that have an inline slot; everything
        // else (other fields, `error`, `detail`, 5xx, network) goes to the
        // banner so nothing is silently dropped.
        const fieldErrors = getApiFieldErrors(error);
        const { companyName: nameError, companyEmail: emailError, ...rest } = fieldErrors;
        setErrors({ companyName: nameError, companyEmail: emailError });
        const serverIntentErrors = pickIntentErrors(rest);
        setIntentErrors(serverIntentErrors);
        const serverLeadSourceErrors = pickLeadSourceErrors(rest);
        setLeadSourceErrors(serverLeadSourceErrors);
        const inlineKeys: string[] = [...INTENT_FIELD_KEYS, ...LEAD_SOURCE_FIELD_KEYS];
        const otherFieldErrors = Object.fromEntries(Object.entries(rest).filter(([key]) => !inlineKeys.includes(key)));
        if (Object.keys(otherFieldErrors).length > 0) {
          setSubmitError(extractApiErrorMessage(otherFieldErrors, t("register.errors.failed")));
        } else if (
          !nameError &&
          !emailError &&
          Object.keys(serverIntentErrors).length === 0 &&
          Object.keys(serverLeadSourceErrors).length === 0
        ) {
          setSubmitError(getApiErrorMessage(error, t("register.errors.failed")));
        }
      },
    },
  });

  const handleBackClick = () => navigate("/");
  const handleSignInClick = () => navigate("/b2b/login");

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarError(null);
    setAvatarPreview(URL.createObjectURL(file));
    setCompanyAvatar("");

    const invalid = validateUploadFile(file, "image");
    if (invalid) {
      setAvatarError(invalid);
      setAvatarPreview(null);
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const res = await uploadFile(file);
      setCompanyAvatar(res.url);
    } catch (err) {
      setAvatarError(getApiErrorMessage(err, t("register.errors.uploadFailed")));
      setAvatarPreview(null);
    } finally {
      setIsUploadingAvatar(false);
    }
    e.target.value = "";
  };

  const handleRemoveAvatar = () => {
    setAvatarPreview(null);
    setCompanyAvatar("");
    setAvatarError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);

    const nextErrors: FormErrors = {};

    if (!companyName.trim()) {
      nextErrors.companyName = t("register.errors.enterCompanyName");
    }

    if (!companyEmail.trim()) {
      nextErrors.companyEmail = t("register.errors.enterCompanyEmail");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyEmail)) {
      nextErrors.companyEmail = t("shared.errors.validEmail");
    }

    setErrors(nextErrors);
    const nextIntentErrors = validateIntent(intent, { required: true });
    setIntentErrors(nextIntentErrors);
    const nextLeadSourceErrors = validateLeadSource(leadSource);
    setLeadSourceErrors(nextLeadSourceErrors);

    if (
      Object.keys(nextErrors).length > 0 ||
      Object.keys(nextIntentErrors).length > 0 ||
      Object.keys(nextLeadSourceErrors).length > 0
    ) {
      return;
    }

    // Call the mutation with the registration data
    registerMutation.mutate({
      data: {
        companyName: companyName.trim(),
        companyIndustry: companyIndustry.trim() || "",
        companyEmail: companyEmail.trim(),
        companyDescription: companyDescription.trim() || "",
        companyAvatar: companyAvatar || undefined,
        ...intentPayload(intent),
        ...leadSourcePayload(leadSource),
      },
    });
  };

  if (submitted) {
    return (
      <div className="auth-page">
        <div className="auth-page__background" />

        <div className="container">
          <div className="auth-page__inner">
            <section className="auth-card auth-card--success" aria-label={t("register.success.ariaLabel")}>
              <div className="auth-card__icon-wrapper">
                <div className="auth-card__icon auth-card__icon--success">
                  <span className="auth-card__icon-glyph" aria-hidden="true">
                    <i className="fa-solid fa-circle-check" />
                  </span>
                </div>
              </div>

              <header className="auth-card__header">
                <h1 className="auth-card__title">{t("register.success.title")}</h1>
                <p className="auth-card__subtitle">{successMessage || t("register.success.defaultMessage")}</p>
              </header>

              <div className="auth-form__divider">
                <span>{t("register.success.nextSteps")}</span>
              </div>

              <p className="auth-form__footer-text auth-form__footer-text--block">
                {t("register.success.nextStepsText")}
              </p>

              <button
                type="button"
                className="auth-form__submit auth-form__submit--primary"
                onClick={handleSignInClick}
              >
                {t("register.success.backToSignIn")}
              </button>
            </section>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-page__background" />

      <div className="container">
        <div className="auth-page__inner">
          <button type="button" className="auth-page__back-link" onClick={handleBackClick}>
            {t("shared.backToPlatformSelection")}
          </button>

          <section className="auth-card" aria-label={t("register.ariaLabel")}>
            <div className="auth-card__icon-wrapper">
              <div className="auth-card__icon auth-card__icon--primary">
                <span className="auth-card__icon-glyph" aria-hidden="true">
                  <i className="fa-solid fa-building-user" />
                </span>
              </div>
            </div>

            <header className="auth-card__header">
              <h1 className="auth-card__title">{t("register.title")}</h1>
              <p className="auth-card__subtitle">{t("register.subtitle")}</p>
            </header>

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="auth-form__field">
                <label className="auth-form__label" htmlFor="reg-company">
                  {t("register.companyName")} <span className="auth-form__required">*</span>
                </label>
                <div
                  className={`auth-form__input-wrapper ${errors.companyName ? "auth-form__input-wrapper--error" : ""}`}
                >
                  <span className="auth-form__input-icon" aria-hidden="true">
                    <i className="fa-solid fa-building" />
                  </span>
                  <input
                    id="reg-company"
                    type="text"
                    className="auth-form__input"
                    placeholder={t("register.companyNamePlaceholder")}
                    autoComplete="organization"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                  />
                </div>
                {errors.companyName ? <p className="auth-form__error">{errors.companyName}</p> : null}
              </div>

              <div className="auth-form__field">
                <label className="auth-form__label" htmlFor="reg-industry">
                  {t("register.companyIndustry")}
                </label>
                <div className="auth-form__input-wrapper">
                  <span className="auth-form__input-icon" aria-hidden="true">
                    <i className="fa-solid fa-briefcase" />
                  </span>
                  <input
                    id="reg-industry"
                    type="text"
                    className="auth-form__input"
                    placeholder={t("register.companyIndustryPlaceholder")}
                    value={companyIndustry}
                    onChange={(e) => setCompanyIndustry(e.target.value)}
                  />
                </div>
              </div>

              <div className="auth-form__field">
                <label className="auth-form__label" htmlFor="reg-email">
                  {t("register.companyEmail")} <span className="auth-form__required">*</span>
                </label>
                <div
                  className={`auth-form__input-wrapper ${errors.companyEmail ? "auth-form__input-wrapper--error" : ""}`}
                >
                  <span className="auth-form__input-icon" aria-hidden="true">
                    <i className="fa-regular fa-envelope" />
                  </span>
                  <input
                    id="reg-email"
                    type="email"
                    className="auth-form__input"
                    placeholder={t("register.companyEmailPlaceholder")}
                    autoComplete="email"
                    value={companyEmail}
                    onChange={(e) => setCompanyEmail(e.target.value)}
                  />
                </div>
                {errors.companyEmail ? <p className="auth-form__error">{errors.companyEmail}</p> : null}
              </div>

              <div className="auth-form__field">
                <label className="auth-form__label" htmlFor="reg-description">
                  {t("register.companyDescription")}
                </label>
                <div className="auth-form__input-wrapper auth-form__input-wrapper--textarea">
                  <textarea
                    id="reg-description"
                    className="auth-form__input auth-form__textarea"
                    placeholder={t("register.companyDescriptionPlaceholder")}
                    rows={4}
                    value={companyDescription}
                    onChange={(e) => setCompanyDescription(e.target.value)}
                  />
                </div>
              </div>

              <div className="auth-form__field">
                <label className="auth-form__label">{t("register.companyAvatar")}</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={uploadAccept("image")}
                  className="auth-form__file-input"
                  onChange={handleAvatarChange}
                  disabled={isUploadingAvatar}
                  aria-label={t("register.uploadAvatar")}
                />
                {isUploadingAvatar && (
                  <p className="auth-form__hint auth-form__hint--uploading">
                    <i className="fa-solid fa-spinner fa-spin" /> {t("common:actions.uploading")}
                  </p>
                )}
                {avatarError && <p className="auth-form__error auth-form__error--block">{avatarError}</p>}
                {(avatarPreview || companyAvatar) && (
                  <div className="auth-form__avatar-preview">
                    {avatarPreview ? (
                      <img src={avatarPreview} alt={t("register.avatarPreview")} className="auth-form__avatar-img" />
                    ) : (
                      <span className="auth-form__avatar-placeholder">
                        <i className="fa-solid fa-check" /> {t("register.uploaded")}
                      </span>
                    )}
                    <button
                      type="button"
                      className="auth-form__avatar-remove"
                      onClick={handleRemoveAvatar}
                      disabled={isUploadingAvatar}
                      aria-label={t("register.removeAvatar")}
                    >
                      <i className="fa-solid fa-times" />
                    </button>
                  </div>
                )}
              </div>

              <div className="auth-form__divider">
                <span>{t("register.tradeProfile")}</span>
              </div>
              <p className="auth-form__hint">{t("register.tradeProfileHint")}</p>
              <CompanyIntentFields
                idPrefix="reg-intent"
                value={intent}
                onChange={setIntent}
                errors={intentErrors}
                required
                disabled={registerMutation.isPending}
              />

              <div className="auth-form__divider">
                <span>{t("register.howYouFoundUs")}</span>
              </div>
              <LeadSourceFields
                idPrefix="reg-source"
                value={leadSource}
                onChange={setLeadSource}
                errors={leadSourceErrors}
                disabled={registerMutation.isPending}
              />

              {submitError && (
                <p className="auth-form__error auth-form__error--block" role="alert">
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                className="auth-form__submit auth-form__submit--primary"
                disabled={registerMutation.isPending}
              >
                {registerMutation.isPending ? t("common:actions.submitting") : t("register.submit")}
              </button>

              <div className="auth-form__divider">
                <span>{t("shared.or")}</span>
              </div>

              <p className="auth-form__footer-text">
                {t("register.haveAccount")}{" "}
                <button
                  type="button"
                  className="auth-form__link"
                  onClick={handleSignInClick}
                  disabled={registerMutation.isPending}
                >
                  {t("shared.signInLink")}
                </button>
              </p>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CompanyRegistrationPage;
